import React from 'react';
import PropTypes from 'prop-types';

import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Grid from '@material-ui/core/Grid';

import TextField from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';

import Button from 'components/common/Button';

import { fetchPrescribersRegistry } from 'redux/modules/prescribersRegistry';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

const styles = theme => ({
  root: {
    margin: '16px 24px',
  },
  gridItemNpi: {
    maxWidth: '19%',
    flexBasis: '19%',
  },
  gridItemState: {
    maxWidth: '15.33334%',
    flexBasis: '15.33334%',
  },
  gridItemButton: {
    maxWidth: '15.66667%',
    flexBasis: '15.66667%',
  },
  textFieldFirstName: {
    display: 'flex',
  },
  textFieldLastName: {
    position: 'relative',
    display: 'flex',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 1,
      left: -2,
      bottom: 1,
      width: 4,
      backgroundColor: theme.palette.grey[100],
      zIndex: 1,
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 8,
      left: -1,
      bottom: 8,
      width: 1,
      backgroundColor: theme.palette.primary.A100,
      zIndex: 2,
    },
  },
  textFieldLastNameInputRoot: {
    display: 'flex',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  textFieldFirstNameInputRoot: {
    display: 'flex',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  textFieldInputRoot: {
    display: 'flex',
  },
  button: {
    width: '100%',
  },
});

const usaStates = new UsaStates();

const validateForm = (values) => {
  const errors = {};

  if (!values['First Name'] && !values['Last Name'] && !values.NPI) {
    errors['First Name'] = 'Please, specify First and Last Name or NPI';
    errors['Last Name'] = 'Please, specify First and Last Name or NPI';
    errors.NPI = 'Please, specify First and Last Name or NPI';
  } else if (!values.NPI) {
    if (values['First Name'] && !values['Last Name']) {
      errors['Last Name'] = 'Please, specify Last Name';
    }
    if (values['Last Name'] && !values['First Name']) {
      errors['First Name'] = 'Please, specify First Name';
    }
  }

  if (values.NPI && validate.minLength10(values.NPI)) {
    errors.NPI = validate.minLength10(values.NPI);
  }

  return errors;
};

function onSubmit(values, dispatch) {
  const errors = validateForm(values);
  if (Object.keys(errors).length > 0) {
    throw new SubmissionError(errors);
  }
  return new Promise((resolve, reject) => {
    dispatch(fetchPrescribersRegistry({
      values,
      resolve,
      reject,
      triggerLoading: true,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

const shouldError = () => true;

const PrescribersSearchDialogFilterForm = ({
  handleSubmit, submit, classes,
}) => (
  <form
    className={classes.root}
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    <Grid
      container
      justify="space-between"
      alignItems="center"
      spacing={16}
    >
      <Grid
        item
        xs={6}
      >
        <Grid
          container
          justify="space-between"
          alignItems="center"
          spacing={0}
        >
          <Grid
            item
            xs={6}
          >
            <Field
              className={classes.textFieldFirstName}
              inputRootClassName={classes.textFieldFirstNameInputRoot}
              id="First Name"
              name="First Name"
              component={TextField}
              placeholder="First Name"
              errorPlacement="bottom"
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            <Field
              className={classes.textFieldLastName}
              inputRootClassName={classes.textFieldLastNameInputRoot}
              id="Last Name"
              name="Last Name"
              component={TextField}
              placeholder="Last Name"
              errorPlacement="bottom"
              fullWidth
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        className={classes.gridItemNpi}
        item
        xs={2}
      >
        <Field
          inputRootClassName={classes.textFieldInputRoot}
          id="NPI"
          name="NPI"
          component={TextField}
          placeholder="NPI"
          validate={[validate.minLength10]}
          normalize={normalize.number}
          errorPlacement="bottom"
          maxLength="10"
          fullWidth
        />
      </Grid>
      <Grid
        className={classes.gridItemState}
        item
        xs={2}
      >
        <Field
          inputRootClassName={classes.textFieldInputRoot}
          id="State"
          name="State"
          placeholder="State"
          component={AutosuggestField}
          normalize={normalize.uppercase}
          fullWidth
          renderSuggestionContent={suggestion => suggestion.abbreviation}
          onSuggestionSelected={suggestion => suggestion.abbreviation}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(usaStates.states.filter(item =>
                  item.abbreviation.indexOf(value) === 0));
              } else {
                resolve(usaStates.states);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      <Grid
        className={classes.gridItemButton}
        item
        xs={2}
      >
        <Button
          className={classes.button}
          onClick={submit}
          variant="raised"
          color="primary"
        >
          Search
        </Button>
      </Grid>
    </Grid>
  </form>
);

PrescribersSearchDialogFilterForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    gridItemNpi: PropTypes.string.isRequired,
    gridItemState: PropTypes.string.isRequired,
    gridItemButton: PropTypes.string.isRequired,
    textFieldFirstName: PropTypes.string.isRequired,
    textFieldFirstNameInputRoot: PropTypes.string.isRequired,
    textFieldLastName: PropTypes.string.isRequired,
    textFieldLastNameInputRoot: PropTypes.string.isRequired,
    textFieldInputRoot: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

PrescribersSearchDialogFilterForm.defaultProps = {
  initialValues: null,
};

export default reduxForm({
  onSubmit,
  validate: validateForm,
  shouldError,
})(withStyles(styles)(PrescribersSearchDialogFilterForm));



// WEBPACK FOOTER //
// ./src/components/settings/PrescribersSearchDialog/PrescribersSearchDialogFilterForm.js